import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, max, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OpenAiSummarizeResponse } from '../models/openai/openai-summarized-response.interface';
import { SelectedPublishTarget } from '../models/selected-publish-target.interface';
import { IPostUpdate } from '../models/post-update.interface';
import { StreamUpdatePayload } from '../models/stream-update-payload.interface';
import { IOpenAiSummarize } from '../models/openai/openai-summarize.interface';
import { IUserPublishTarget } from '../models/account.interface';
import { ICopilotContext, IOpenAiCompletion } from '../models/openai/openai-completion.interface';
import { IOpenAiCraft } from '../models/openai/openai-craft.interface';
import { IOpenAiSocialCard } from '../models/openai/openai-social-card.interface';
import { IAiChatMessage } from '../models/ai-chat-message.interface';
import { IAiConversation, IAiConversationListResult, IAiConversationMessage } from '../models/ai-conversation.interface';
import { IAiModel } from '../models/ai-model.interface';
import { AuthService } from './guard/auth.service';

@Injectable({
  providedIn: 'root'
})
export class OpenAiService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient, private auth: AuthService) { }

  /**
   * Single-turn AI completion.
   * @param message Prompt text.
   * @param role Message role.
   * @param accountId Active account ID.
   * @param publishTarget Optional publish target context.
   */
  completion(message: string, role: string, accountId: string, publishTarget: IUserPublishTarget | null): Observable<unknown> {
    const url = `${this.apiUrl}/ai/completion`;
    const body: IOpenAiCompletion = {
      accountId,
      message,
      role: role as IOpenAiCompletion['role'],
    };
    return this.http.post(url, body, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  /**
   * Multi-turn chat completion. Sends the full conversation history so the
   * model maintains context across turns.
   * @param messages Ordered list of user/assistant turns.
   * @param systemPrompt Optional system context injected before the conversation.
   * @param accountId Active account ID.
   * @param model Optional model override (e.g. 'gpt-4o'). Falls back to the account default.
   * @param copilotMode Optional editor copilot mode when the request comes from Em.
   * @param copilotContext Optional post-editor context for server-side command detection.
   */
  chat(messages: IAiChatMessage[], systemPrompt: string | undefined, accountId: string, model?: string, copilotMode?: 'agent' | 'ask', copilotContext?: ICopilotContext): Observable<unknown> {
    const url = `${this.apiUrl}/ai/completion`;
    const body: IOpenAiCompletion = {
      accountId,
      messages,
      systemPrompt,
      role: 'user',
      ...(model ? { model } : {}),
      ...(copilotMode ? { copilotMode } : {}),
      ...(copilotContext ? { copilotContext } : {}),
    };
    return this.http.post(url, body, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  /**
   * Record client-side copilot command application issues for admin auditing.
   */
  logCopilotClientIssue(
    accountId: string,
    commandType: string,
    reason: string,
    details?: string,
    prompt?: string,
  ): Observable<{ success: boolean }> {
    const url = `${this.apiUrl}/ai/copilot/client-issue`;
    return this.http.post<{ success: boolean }>(url, {
      accountId,
      issueType: 'apply-command-failed',
      commandType,
      reason,
      ...(details ? { details } : {}),
      ...(prompt ? { prompt } : {}),
    }, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }


  /**
   * Fetches the active AI model roster for the copilot's model picker.
   */
  getActiveModels(): Observable<IAiModel[]> {
    const url = `${this.apiUrl}/ai/models/active`;
    return this.http.get<IAiModel[]>(url);
  }

  /**
   * Lists the current user's saved copilot conversations, most recent first.
   */
  listConversations(page: number = 1, limit: number = 20): Observable<IAiConversationListResult> {
    const url = `${this.apiUrl}/ai/conversations?page=${page}&limit=${limit}`;
    return this.http.get<IAiConversationListResult>(url);
  }

  /**
   * Fetches one saved conversation, including all messages.
   */
  getConversation(id: string): Observable<IAiConversation> {
    const url = `${this.apiUrl}/ai/conversations/${id}`;
    return this.http.get<IAiConversation>(url);
  }

  /**
   * Deletes a saved conversation.
   */
  deleteConversation(id: string): Observable<{ success: boolean }> {
    const url = `${this.apiUrl}/ai/conversations/${id}`;
    return this.http.delete<{ success: boolean }>(url);
  }

  /**
   * Creates or updates (via payload.id) the current copilot conversation so
   * it survives a panel close or page reload.
   */
  saveConversation(payload: { id?: string; accountId?: string; model?: string; messages: IAiConversationMessage[]; systemPrompt?: string }): Observable<{ id: string; success: boolean }> {
    const url = `${this.apiUrl}/ai/conversations`;
    return this.http.post<{ id: string; success: boolean }>(url, payload, { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) });
  }

  /**
   * Craft a social media post
   * @param content content
   * @param accountId account id
   * @param publishTargetId publish target name
   * @returns 
   */
  craft(content: string, accountId: string, publishTargetId: string): Observable<any> {
    const url = `${this.apiUrl}/ai/craft`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    const body: IOpenAiCraft = {
      'content': content,
      'accountId': accountId,
      'publishTargetId': publishTargetId,
      'role': 'assistant',
      // 'model': environment.openAiModel
    };

    return this.http.post(url, body, options);
  }



  summarize(website: string, maxParagraphs: number, tone: string, directions: string, role: any, publishTarget: string, prompt: string, accountId: string, userId: string): Observable<any> {
    const url = `${this.apiUrl}/ai/summarize`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    const body: IOpenAiSummarize = {
      'website': website,
      'maxParagraphs': maxParagraphs,
      'tone': tone,
      'directions': directions,
      'accountId': accountId,
      'userId': userId,
      'role': role,
      // 'model': environment.openAiModel,
      'publishTarget': publishTarget,
      'prompt': prompt
    };

    const timeoutDuration = 60000;

    return this.http.post(url, body, options).pipe(
      timeout(timeoutDuration),
      catchError((error) => {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          status: error.status,
          error,
        });
        return throwError(() => error);
      })
    );
  }


  /**
   * Summarize content for multiple targets
   * @param targets Array of publish targets
   * @param post Content to summarize
  * @param directions Additional AI directions
   * @returns Observable with all summaries
   */
  summarizeMultiple(
    targets: SelectedPublishTarget[],
    post: IPostUpdate,
    directions: string
  ): Observable<OpenAiSummarizeResponse> {
    const url = `${this.apiUrl}/ai/summarize-multiple`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    const summaryPost: IPostUpdate = { ...post };
    delete summaryPost.image;
    delete summaryPost.socialCard;
    const body = {
      targets,
      post: summaryPost,
      directions,
      // model: environment.openAiModel,
    };

    const timeoutDuration = 60000;

    return this.http.post<OpenAiSummarizeResponse>(url, body, options).pipe(
      timeout(timeoutDuration),
      catchError((error) => {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          status: error.status,
          error,
        });
        return throwError(() => error);
      })
    );
  }


  /**
   * Summarize content for multiple targets via streaming
   * @param targets Array of publish targets
   * @param post Content to summarize
   * @param directions Additional AI directions
   * @returns Observable that emits OpenAiSummarizeResponse
   */
  summarizeStream(
    website: string,
    maxParagraphs: number,
    tone: string,
    directions: string,
    role: IOpenAiSummarize['role'],
    publishTarget: string,
    prompt: string,
    accountId: string,
    userId: string,
  ): Observable<StreamUpdatePayload> {
    const url = `${this.apiUrl}/ai/summarize`;

    const body: IOpenAiSummarize = {
      website,
      maxParagraphs,
      tone,
      directions,
      userId,
      accountId,
      role,
      // model: environment.openAiModel,
      publishTarget,
      prompt,
    };

    return new Observable<StreamUpdatePayload>((observer) => {
      const controller = new AbortController();

      this.auth.ensureValidAccessToken().then((authToken) => {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }

        return fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      })
        .then(async (response) => {
          if (!response.ok || !response.body) {
            observer.error(new Error(`HTTP ${response.status}`));
            return;
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\x1E');
            buffer = parts.pop() ?? '';

            for (const part of parts) {
              const trimmed = part.trim();
              if (!trimmed) {
                continue;
              }

              try {
                const payload: StreamUpdatePayload = JSON.parse(trimmed);
                observer.next(payload);
                if (
                  payload.status === 'COMPLETE' ||
                  payload.status === 'ERROR' ||
                  payload.status === 'ERROR_MODERATION'
                ) {
                  observer.complete();
                  return;
                }
              } catch {
                // Ignore malformed chunks and continue streaming.
              }
            }
          }

          observer.complete();
        })
        .catch((error: unknown) => {
          if (error instanceof Error && error.name === 'AbortError') {
            observer.complete();
            return;
          }
          observer.error(error);
        });

      return () => {
        controller.abort();
      };
    });
  }


  /**
   * Create a social card
   * @param title 
   * @param content 
   * @param image 
   * @returns 
   */
  createSocialCard(title: string, content: string, image: string): Promise<IOpenAiSocialCard> {
    const url = `${this.apiUrl}/ai/social-card`;

    const body = {
      title,
      content,
      image,
      // model: environment.openAiModel,
    };

    return this.http.post<IOpenAiSocialCard>(url, body).toPromise();
  }


  /**
   * Create a title based on body content
   */
  summarizeTitle(content: string, tone: string, directions: string, role: any, publishTarget: string, accountId: string, userId: string): Observable<any> {
    const url = `${this.apiUrl}/ai/summarize-title`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };
    const body: IOpenAiSummarize = {
      'content': content,
      'tone': tone,
      'directions': directions,
      'accountId': accountId,
      'userId': userId,
      'role': role,
      // 'model': environment.openAiModel,
      'publishTarget': publishTarget,
    };

    const timeoutDuration = 60000;

    return this.http.post(url, body, options).pipe(
      timeout(timeoutDuration),
      catchError((error) => {
        console.error('Error details:', {
          name: error.name,
          message: error.message,
          status: error.status,
          error,
        });
        return throwError(() => error);
      })
    );
  }


  /**
   * Generate a concise image search term (and return title/blurb) for a post
   * Calls server route: POST /openai/image-search-term
   * @param post IPostUpdate or minimal post payload
   */
  imageSearchTerm(post: IPostUpdate | any): Observable<string> {
    const url = `${this.apiUrl}/ai/image-search-term`;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    };

    const body = {
      post,
      // model: environment.openAiModel
    };

    const timeoutDuration = 120000; // 2 minutes 

    return this.http.post<{ success: boolean; imageSearchTerm?: string; }>(url, body, options).pipe(
      timeout(timeoutDuration),
      map(response => {
      if (response.success && response.imageSearchTerm) {
        return response.imageSearchTerm;
      } else {
        throw new Error('Failed to generate image search term');
      }
      }),
      catchError((error) => {
      console.error('Error details:', {
        name: error?.name,
        message: error?.message,
        status: error?.status,
        error,
      });
      return throwError(() => error);
      })
    );
  }

  /**
   * Submit a thumbs-up or thumbs-down rating for an AI response.
   * The API enforces a single vote per (userId, promptText, responseText) tuple.
   * @param promptText The user's prompt that generated the response.
   * @param responseText The AI response text being rated.
   * @param rating 'up' or 'down'.
   * @param accountId Active account ID (optional context).
   * @param context Panel context string (e.g. 'post-editor').
   * @param aiModel The model identifier that generated the response (e.g. 'gpt-4o').
   */
  submitFeedback(
    promptText: string,
    responseText: string,
    rating: 'up' | 'down',
    accountId?: string,
    context?: string,
    aiModel?: string,
  ): Observable<{ success: boolean }> {
    const url = `${this.apiUrl}/ai/feedback`;
    const body = { promptText, responseText, rating, accountId, context, aiModel };
    return this.http.post<{ success: boolean }>(url, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    });
  }

}
